import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const blank = { name: "", email: "", password: "", role: "admin", is_active: true };

export default function Users() {
    const { user: me } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blank);

    const load = async () => setItems((await api.get("/admin/users")).data);
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
    const openEdit = (u) => {
        setEditing(u);
        setForm({ name: u.name, email: u.email, password: "", role: u.role, is_active: u.is_active !== false });
        setOpen(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                const payload = { name: form.name, email: form.email, role: form.role, is_active: form.is_active };
                if (form.password) payload.password = form.password;
                await api.patch(`/admin/users/${editing.id}`, payload);
                toast.success("User updated");
            } else {
                await api.post("/admin/users", { name: form.name, email: form.email, password: form.password, role: form.role });
                toast.success("Admin added");
            }
            setOpen(false);
            setEditing(null);
            setForm(blank);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete admin?")) return;
        try { await api.delete(`/admin/users/${id}`); load(); toast.success("Deleted"); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="p-8 max-w-5xl" data-testid="users-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">Access</div>
                    <h1 className="font-display text-4xl font-bold text-espresso tracking-tight">Admin users</h1>
                </div>
                <Button onClick={openCreate} className="bg-espresso rounded-sm" data-testid="new-user-button"><Plus className="w-4 h-4 mr-1"/>Add admin</Button>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blank); } }}>
                    <DialogContent className="rounded-sm">
                        <DialogHeader>
                            <DialogTitle className="font-display text-espresso">{editing ? "Edit admin user" : "Add admin user"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={onSubmit} className="space-y-3" data-testid="user-form">
                            <div><Label className="text-xs uppercase text-[#7A726D]">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-sm mt-1" data-testid="user-name-input"/></div>
                            <div><Label className="text-xs uppercase text-[#7A726D]">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="rounded-sm mt-1" data-testid="user-email-input"/></div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">{editing ? "New password (leave blank to keep current)" : "Password"}</Label>
                                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} minLength={editing ? 0 : 6} className="rounded-sm mt-1" data-testid="user-password-input"/>
                            </div>
                            <div>
                                <Label className="text-xs uppercase text-[#7A726D]">Role</Label>
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger className="rounded-sm mt-1" data-testid="user-role-select"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin">Admin</SelectItem>
                                        <SelectItem value="master">Master</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {editing && (
                                <div className="flex items-center justify-between bg-[#F9F8F6] p-3 rounded-sm">
                                    <div>
                                        <div className="text-sm font-medium text-espresso">Active</div>
                                        <div className="text-xs text-[#7A726D]">Inactive users cannot sign in.</div>
                                    </div>
                                    <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="user-active-switch" disabled={editing?.id === me?.id}/>
                                </div>
                            )}
                            <Button type="submit" className="bg-espresso w-full rounded-sm" data-testid="user-submit-button">
                                {editing ? "Save changes" : "Create"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                <CardContent className="p-0">
                    <Table data-testid="users-table">
                        <TableHeader>
                            <TableRow className="bg-[#F9F8F6]">
                                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((u) => (
                                <TableRow key={u.id}>
                                    <TableCell className="font-medium">{u.name}</TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell><span className={`px-2 py-0.5 rounded-sm text-xs uppercase ${u.role === "master" ? "bg-[#3E2723] text-white" : "bg-[#E5E0D8] text-[#3E2723]"}`}>{u.role}</span></TableCell>
                                    <TableCell>
                                        {u.is_active !== false ? <span className="text-sage text-xs uppercase">Active</span> : <span className="text-[#7A726D] text-xs uppercase">Disabled</span>}
                                    </TableCell>
                                    <TableCell className="text-right space-x-2">
                                        <button onClick={() => openEdit(u)} className="text-[#C48464] hover:text-espresso inline-flex" data-testid={`user-edit-${u.id}`}>
                                            <Pencil className="w-4 h-4"/>
                                        </button>
                                        {u.role !== "master" && u.id !== me?.id && (
                                            <button onClick={() => onDelete(u.id)} className="text-[#B24A45] inline-flex" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4"/></button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
